import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react'

import {
  Button, Check, Icon, ShareOutline,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string } from 'prop-types'

import { buildImagePath, iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import CopiedTooltip from './Components/CopiedTooltip/CopiedTooltip'

const shareIcon = <Icon svgComponent={ShareOutline} size={iconSize} notSpaced color="darker" />
const copiedIcon = <Icon svgComponent={Check} size={iconSize} notSpaced color="darker" />

export const COPIED_FEEDBACK_MS = 1000

// Copies the image's /ai-image/:slug page URL. A successful copy turns the icon into a check and shows a "Link copied"
// tooltip for a moment, and announces it; a failed one leaves the control as it was.
function ShareButton({ slug, customClass = '' }) {
  const t = useTranslation()
  const [copiedAnchor, setCopiedAnchor] = useState(null)
  const isCopied = Boolean(copiedAnchor)
  const labelKey = isCopied ? 'copiedLabel' : 'label'
  const wrapRef = useRef(null)
  const resetTimerRef = useRef(null)

  useEffect(() => () => clearTimeout(resetTimerRef.current), [])

  // The Clipboard API is absent outside a secure context, so the control does nothing there
  // rather than throwing on click. A repeat click restarts the confirmation.
  const handleShare = useCallback(() => {
    if (!navigator.clipboard?.writeText) return

    navigator.clipboard.writeText(`${window.location.origin}${buildImagePath(slug)}`)
      .then(() => {
        if (!wrapRef.current) return

        const { top, left, width } = wrapRef.current.getBoundingClientRect()
        setCopiedAnchor({ top, centerX: left + width / 2 })
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = setTimeout(() => setCopiedAnchor(null), COPIED_FEEDBACK_MS)
      })
      .catch(() => {})
  }, [slug])

  return (
    <span ref={wrapRef} className="image-assistant-edit-tab__share-wrap">
      <Button
        type="secondary"
        size="small"
        variant="only-icon"
        actionType="button"
        customClass={customClass}
        icon={isCopied ? copiedIcon : shareIcon}
        aria-label={t(`imageAssistantPanel.editTab.share.${labelKey}`)}
        onClick={handleShare}
      />
      {isCopied && (
        <CopiedTooltip anchor={copiedAnchor} label={t('imageAssistantPanel.editTab.share.copiedLabel')} />
      )}
      <span className="image-assistant-edit-tab__share-status" role="status">
        {isCopied ? t('imageAssistantPanel.editTab.share.copiedStatus') : ''}
      </span>
    </span>
  )
}

ShareButton.propTypes = {
  slug: string.isRequired,
  customClass: string,
}

export default memo(ShareButton)
